// The visit (encounter) lifecycle. The visit is the spine every clinical and financial
// record hangs from (CLAUDE.md, domain rule 1), so the rules for how it moves are kept in
// one place.
//
//   open ──close──▶ closed      (consultation and billing slices; not exposed yet)
//   open ──cancel─▶ cancelled   ("opened in error", reason required)
//
// closed and cancelled are final. Reopening a visit is a later decision, and when it is
// made it is added here, not in a route handler.
//
// Cancelling is allowed only while nothing clinical or financial references the visit;
// that fact comes from the database, so the caller passes it in.

import type { Result } from './result.ts';
import { err, ok } from './result.ts';
import { eatDayOf, daysEqual } from './time.ts';

export const VISIT_STATUSES = ['open', 'closed', 'cancelled'] as const;
export type VisitStatus = (typeof VISIT_STATUSES)[number];

/**
 * Visit types. `inpatient` is opened only by admitting a patient from another visit
 * (docs/plans/phase2-inpatient-adt.md), never at check-in. `emergency` is a casualty visit,
 * opened only at a casualty service point (docs/plans/phase2-casualty.md §1.1); day case
 * follows later in Phase 2. `mortuary` is a deceased person's time in the mortuary, opened only
 * by receiving the body (docs/plans/phase2-mortuary-and-notifications.md §1.1 item 4); it
 * carries the storage charges and is never a patient visit in reports.
 */
export const VISIT_TYPES = ['outpatient', 'walk_in', 'inpatient', 'emergency', 'mortuary'] as const;
export type VisitType = (typeof VISIT_TYPES)[number];

/** The types reception opens at check-in; `emergency` only at a casualty point. */
export const CHECK_IN_VISIT_TYPES = ['outpatient', 'walk_in', 'emergency'] as const;
export type CheckInVisitType = (typeof CHECK_IN_VISIT_TYPES)[number];

export type VisitAction = 'close' | 'cancel';

export type VisitTransitionErrorCode =
  'visit.not_open' | 'visit.has_records' | 'visit.reason_required';

/** English descriptions; the API and the web app own the wording shown to a user. */
export const VISIT_TRANSITION_ERRORS: Record<VisitTransitionErrorCode, string> = {
  'visit.not_open': 'Only an open visit can be closed or cancelled',
  'visit.has_records': 'A visit with clinical or billing records cannot be cancelled',
  'visit.reason_required': 'Cancelling a visit needs a reason',
};

export interface VisitTransitionInput {
  readonly from: VisitStatus;
  readonly action: VisitAction;
  /** Required for cancel. */
  readonly reason?: string | null | undefined;
  /** Whether any clinical or financial record references the visit. Required for cancel. */
  readonly hasRecords?: boolean | undefined;
}

export function visitTransition(
  input: VisitTransitionInput,
): Result<VisitStatus, VisitTransitionErrorCode> {
  if (input.from !== 'open') return err('visit.not_open');
  if (input.action === 'close') return ok('closed');
  if ((input.reason ?? '').trim() === '') return err('visit.reason_required');
  // Unknown is treated as "has records": cancelling must be proven safe, not assumed.
  if (input.hasRecords !== false) return err('visit.has_records');
  return ok('cancelled');
}

/**
 * How far ahead of the server's clock a client-sent arrival time may be and still count as
 * "now": workstation clocks drift. This is clock tolerance, not a clinical rule.
 */
export const ARRIVAL_CLOCK_SKEW_MS = 2 * 60_000;

export type ArrivalTimeErrorCode = 'visit.arrival_in_future' | 'visit.arrival_not_today';

export const ARRIVAL_TIME_ERRORS: Record<ArrivalTimeErrorCode, string> = {
  'visit.arrival_in_future': 'The arrival time is in the future',
  'visit.arrival_not_today': 'The arrival time can only be earlier today (East Africa Time)',
};

/**
 * Checks an arrival time reception has backdated (plan §12, decision 13): no later than
 * now, and on the same East Africa day as now. The reason for backdating is required by
 * the request schema and recorded with the visit and in the audit log.
 */
export function checkArrivalTime(arrivedAt: Date, now: Date): Result<Date, ArrivalTimeErrorCode> {
  if (Number.isNaN(arrivedAt.getTime()) || Number.isNaN(now.getTime())) {
    throw new RangeError('Not a valid instant');
  }
  if (arrivedAt.getTime() > now.getTime() + ARRIVAL_CLOCK_SKEW_MS) {
    return err('visit.arrival_in_future');
  }
  // A time within the skew allowance is treated as now, never as a future arrival.
  const arrival = arrivedAt.getTime() > now.getTime() ? now : arrivedAt;
  if (!daysEqual(eatDayOf(arrival), eatDayOf(now))) return err('visit.arrival_not_today');
  return ok(arrival);
}
